// src/auth/UserManagement.js
// Admin panel for managing users
import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from './AuthContext';
import './UserManagement.css';

const UserManagement = ({ isOpen, onClose }) => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', name: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const res = await fetch('/api/auth/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Failed to load users:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      setError('');
      setSuccess('');
    }
  }, [isOpen, loadUsers]);

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!newUser.email || !newUser.password) {
      setError('Email et mot de passe requis');
      return;
    }
    if (newUser.password.length < 6) {
      setError('Mot de passe: 6 caractères minimum');
      return;
    }

    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(newUser)
      });

      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Erreur lors de la création');
        return;
      }

      setSuccess(`Utilisateur "${data.user.name}" créé avec succès`);
      setNewUser({ email: '', password: '', name: '' });
      setShowAddForm(false);
      loadUsers();
    } catch (err) {
      setError('Erreur réseau');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Supprimer l'utilisateur "${userName}" ?`)) return;

    try {
      const res = await fetch(`/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        setSuccess(`Utilisateur "${userName}" supprimé`);
        loadUsers();
      } else {
        const data = await res.json();
        setError(data.error || 'Erreur lors de la suppression');
      }
    } catch (err) {
      setError('Erreur réseau');
    }
  };

  if (!isOpen) return null;

  const modal = (
    <div className="user-mgmt-overlay" onClick={onClose}>
      <div className="user-mgmt-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-mgmt-header">
          <h2>Gestion des utilisateurs</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="user-mgmt-body">
          {error && <div className="user-mgmt-error">{error}</div>}
          {success && <div className="user-mgmt-success">{success}</div>}

          {loading ? (
            <p className="user-mgmt-loading">Chargement...</p>
          ) : (
            <div className="user-list">
              {users.map(u => (
                <div key={u.id} className="user-row">
                  <div className="user-row-info">
                    <span className="user-row-name">{u.name || u.email}</span>
                    <span className="user-row-email">{u.email}</span>
                  </div>
                  <span className={`user-role-badge ${u.role}`}>{u.role}</span>
                  {u.role !== 'admin' && (
                    <button
                      className="user-delete-btn"
                      onClick={() => handleDeleteUser(u.id, u.name || u.email)}
                      title="Supprimer"
                    >
                      &times;
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {showAddForm ? (
            <form onSubmit={handleAddUser} className="add-user-form">
              <h3>Nouvel utilisateur</h3>
              <input
                type="text"
                placeholder="Nom"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
              />
              <input
                type="text"
                placeholder="Identifiant"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                required
              />
              <input
                type="password"
                placeholder="Mot de passe (6 car. min)"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                required
                minLength={6}
              />
              <div className="add-user-actions">
                <button type="button" className="cancel-btn" onClick={() => setShowAddForm(false)}>
                  Annuler
                </button>
                <button type="submit" className="confirm-btn">
                  Créer
                </button>
              </div>
            </form>
          ) : (
            <button className="add-user-btn" onClick={() => setShowAddForm(true)}>
              + Ajouter un utilisateur
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default UserManagement;
